import { useState } from "react";
import { motion } from "framer-motion";
import { Clock, CheckSquare, CheckCheck } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import ArrowDisplay from "@/components/ArrowDisplay";
import { formatDate } from "./useReviewData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PendingReviewSection = ({ items, approveSettings, updateApproveSettings, approveStratagem, onBulkApproved }) => {
  const [bulkLoading, setBulkLoading] = useState(false);

  if (items.length === 0) return null;

  const getNextHour = () => {
    const now = new Date();
    now.setHours(now.getHours() + 1);
    now.setMinutes(0, 0, 0);
    return now.toISOString().slice(0, 16);
  };

  const handleBulkApprove = async () => {
    if (!window.confirm(`Approve all ${items.length} pending stratagem(s)? They will go live within the hour.`)) return;
    setBulkLoading(true);
    try {
      const res = await axios.post(`${API}/stratagems/bulk-approve`, {}, { withCredentials: true });
      toast.success(`Bulk approved ${res.data.approved} stratagem(s)`);
      if (onBulkApproved) onBulkApproved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk approve failed");
    } finally {
      setBulkLoading(false);
    }
  };

  return (
    <motion.section
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.1 }}
      className="mt-8 hud-card clip-corners p-6 border-2 border-yellow-500/50"
      data-testid="pending-review-section"
    >
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <Clock className="h-6 w-6 text-yellow-400" />
          <h2 className="font-headings text-2xl uppercase tracking-widest text-yellow-400">
            Pending Review ({items.length})
          </h2>
        </div>
        {items.length > 1 && (
          <button
            onClick={handleBulkApprove}
            disabled={bulkLoading}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-500 disabled:opacity-50 disabled:cursor-not-allowed px-4 py-2 text-xs font-data text-white"
            data-testid="bulk-approve-button"
          >
            <CheckCheck className="h-4 w-4" />
            {bulkLoading ? "Approving..." : `Approve All (${items.length})`}
          </button>
        )}
      </div>
      <p className="text-sm text-gray-400 mb-4">
        New stratagems detected from GitHub. Approve to add to the app, or they auto-approve after 5 days.
      </p>
      <div className="space-y-4">
        {items.map((item) => {
          const settings = approveSettings[item.id] || { releaseDate: getNextHour(), newDays: 15 };
          return (
            <div key={item.id} className="border border-yellow-500/30 p-4 bg-yellow-500/5" data-testid={`pending-item-${item.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="text-xs uppercase tracking-[0.3em] text-gray-500">{item.category}</p>
                  <h3 className="mt-1 font-headings text-xl uppercase tracking-wide text-white">{item.name}</h3>
                  <div className="mt-2 flex items-center gap-3 text-xs text-gray-400">
                    <span>Detected: {formatDate(item.first_synced)}</span>
                    <span className="text-yellow-400">Auto-approves in {item.auto_approve_in || 5} day(s)</span>
                  </div>
                  <div className="mt-2">
                    <ArrowDisplay sequence={item.code} size="sm" testIdPrefix={`pending-${item.id}`} />
                  </div>
                </div>
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-400 w-24">Release:</label>
                    <input
                      type="datetime-local"
                      value={settings.releaseDate}
                      onChange={(e) => updateApproveSettings(item.id, "releaseDate", e.target.value)}
                      className="bg-black/50 border border-white/20 text-white font-data text-xs py-1 px-2"
                      data-testid={`pending-release-${item.id}`}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-400 w-24">New for:</label>
                    <input
                      type="number"
                      value={settings.newDays}
                      onChange={(e) => updateApproveSettings(item.id, "newDays", e.target.value)}
                      className="w-16 bg-black/50 border border-white/20 text-white font-data text-xs py-1 px-2"
                      min="0"
                      max="90"
                      data-testid={`pending-days-${item.id}`}
                    />
                    <span className="text-xs text-gray-400">days</span>
                  </div>
                  <button
                    onClick={() => approveStratagem(item)}
                    className="flex items-center gap-2 bg-green-600 hover:bg-green-500 px-4 py-2 text-xs font-data text-white"
                    data-testid={`pending-approve-${item.id}`}
                  >
                    <CheckSquare className="h-4 w-4" />
                    Approve
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </motion.section>
  );
};

export default PendingReviewSection;
